/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description utils for the react router
 */
import _ from 'lodash';
import { generatePath } from 'react-router-dom';

import { ROUTES } from 'configs/routes';
import { CODE_SET_MAPPING } from 'app-constants/codeExplorer';

/**
 * Eligibility Requests Tab
 */
const ELIGIBILITY_REQUESTS_TABS = {
	tabKey: 'tab',
	values: {
		all: 'all',
		batch: 'batch',
	},
};

/**
 * Eligibility Requests Tab keys
 */
const ELIGIBILITY_REQUESTS_TAB_KEYS = _.values(ELIGIBILITY_REQUESTS_TABS.values);

/**
 * query param keys for entities
 */
const searchParamsKeys = {
	claimStatusRequestId: 'claimStatusId',
	eligibilityRequestId: 'eligibilityId',
	helpQuestion: 'qn',
};

const newRequestKeys = {
	singlePatient: 'single-patient',
	batch: 'batch-upload',
};

/**
 * get logout options for keycloak api
 * @returns {Record<string,string>} logout options
 */
const getLogoutOptions = (): Record<'redirectUri', string> => {
	return {
		redirectUri: globalThis.decodeURIComponent(
			generatePath(ROUTES.logout.redirect, {
				origin: globalThis.location.origin,
			}),
		),
	};
};

/**
 * get home path
 * @returns {string} path
 */
const getHomePath = (): string => {
	return generatePath(ROUTES.home.landing);
};

/**
 * get logout path
 * @returns {string} path
 */
const getLogoutPath = (): string => {
	return generatePath(ROUTES.logout.landing);
};

/**
 * eligibility request create path
 * @returns {string} path
 */
const getEligibilityNewRequestPath = (): string => {
	return generatePath(ROUTES.eligibility.newRequest);
};

/**
 * Edit eligibility request create path
 * @param {string} id
 * @returns {string} path
 */
const getEditEligibilityRequestPath = (id: string): string => {
	return generatePath(ROUTES.eligibility.editRequest, { id });
};

/**
 * View eligibility response path
 * @param {string} id
 * @returns {string} path
 */
const getEligibilityResponsePath = (id: string): string => {
	return generatePath(ROUTES.eligibility.viewResponse, { id });
};

/**
 * get edit batch upload path
 * @returns {string} path
 */
const getEditBatchRequestPath = (id: string): string => {
	const path = ROUTES.eligibility.batch.edit;
	return generatePath(path, { id });
};

/**
 * get batch response path
 * @returns {string} path
 */
const getBatchResponsePath = (id: string): string => {
	const path = ROUTES.eligibility.batch.viewResponse;
	return generatePath(path, { id });
};

/**
 * Validate Eligibility Requests Tab
 * @param {string} tab tab
 * @returns {string} validated tab
 */
const validateEligibilityRequestsTab = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string | undefined => {
	if (_.includes(ELIGIBILITY_REQUESTS_TAB_KEYS, tab)) {
		return tab;
	}
	if (returnDefault) {
		return _.first(ELIGIBILITY_REQUESTS_TAB_KEYS);
	}
	return undefined;
};

/**
 * get eligibility response path
 * @returns {string} path
 */
const getEligibilityRequestsPath = ({ tab, returnDefault }: { tab?: string; returnDefault?: boolean }): string => {
	return generatePath(ROUTES.eligibility.responses, {
		[ELIGIBILITY_REQUESTS_TABS.tabKey]: validateEligibilityRequestsTab({ tab, returnDefault }),
	});
};

/**
 * eligibility batch request create path
 * @returns {string} path
 */
const getBatchEligibilityRequestPath = (id?: string): string => {
	return generatePath(ROUTES.eligibility.batch.add, { id });
};

/**
 * getEligibilityTabPath
 * @param {string} tab
 * @returns {string} path
 */
const getNewEligibilityRequestPath = ({ tab, claimStatusRequestId }: { tab?: string; claimStatusRequestId?: string }): string => {
	if (claimStatusRequestId) {
		const pagePath = generatePath(ROUTES.eligibility.newRequest, { tab: newRequestKeys.singlePatient });
		const searchParams = `?${searchParamsKeys.claimStatusRequestId}=${encodeURIComponent(claimStatusRequestId)}`;
		return `${pagePath}${searchParams}`;
	}

	return generatePath(ROUTES.eligibility.newRequest, { tab });
};

/**
 * get claim status ids from search params
 * @param {string} search
 * @returns {string[] | undefined} claimStatusIDs
 */
const getClaimsIdsFromSearch = (search: string): string[] | undefined => {
	const searchParams = new URLSearchParams(search)?.get(searchParamsKeys.claimStatusRequestId);
	const claimStatusIDs = _.compact(_.split(searchParams, ','));

	if (!_.isEmpty(claimStatusIDs)) {
		return claimStatusIDs;
	}
};

/**
 * New Claim Status request create path
 * @returns {string} path
 */
const getClaimsNewRequestPath = (): string => {
	return generatePath(ROUTES.claims.newRequest);
};

/**
 * View claim status response path
 * @param {string} id
 * @returns {string} path
 */
const getClaimResponsePath = (id: string): string => {
	return generatePath(ROUTES.claims.viewResponse, { id });
};

/**
 * claim status response list path
 * @returns {string} path
 */
const getClaimsRequestPath = (idsList?: string[]): string => {
	const pagePath = ROUTES.claims.responses;
	const searchParams = _.isEmpty(idsList) ? '' : `?${searchParamsKeys.claimStatusRequestId}=${encodeURIComponent(_.join(idsList, ','))}`;
	return generatePath(`${pagePath}${searchParams}`);
};

/**
 * Edit claim status request path
 * @param {string} id
 * @returns {string} path
 */
const getEditClaimRequestPath = (id: string): string => {
	const path = ROUTES.claims.editRequest;
	return generatePath(path, { id });
};

/**
 * claim Status Input Form Path
 * @returns {string} path
 */
const claimStatusInputFormPath = (eligibilityRequestId?: string): string => {
	const pagePath = generatePath(ROUTES.claims.newRequest);
	const searchParams = eligibilityRequestId
		? `?${searchParamsKeys.eligibilityRequestId}=${encodeURIComponent(eligibilityRequestId)}`
		: '';
	return `${pagePath}${searchParams}`;
};

/**
 * Edit eligibility request create path
 * @param {string} id
 * @returns {string} path
 */
const getEditPayerDetailsPath = (id: string, tab?: string): string => {
	return generatePath(ROUTES.settings.dataManagement.payers.edit, { id, tab });
};

/**
 * Payers path
 * @returns {string} path
 */
const getManagePayersPath = (id?: string): string => {
	const path = generatePath(ROUTES.settings.dataManagement.payers.listView);
	if (id) {
		return `${path}#${id}`;
	}
	return path;
};

/**
 * AddProvider create path
 * @returns {string} path
 */
const getAddProviderPath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.providers.add);
};

/**
 * EditProvider create path
 * @param {string} npi
 * @returns {string} path
 */
const getEditProviderPath = (npi: string): string => {
	return generatePath(ROUTES.settings.dataManagement.providers.edit, { id: npi });
};

/**
 * get provider path
 * @returns {string} path
 */
const getProviderPath = (id?: string): string => {
	const path = ROUTES.settings.dataManagement.providers.listView;
	if (id) {
		return `${path}#${id}`;
	}
	return path;
};

/**
 * AddLocations create path
 * @returns {string} path
 */
const getAddLocationPath = (): string => {
	return generatePath(ROUTES.settings.dataManagement.locations.add);
};

/**
 * EditLocation create path
 * @param {string} id
 * @returns {string} path
 */
const getEditLocationPath = (id: string): string => {
	return generatePath(ROUTES.settings.dataManagement.locations.edit, { id });
};

/**
 * get location path
 * @returns {string} path
 */
const getLocationPath = (id?: string): string => {
	const path = ROUTES.settings.dataManagement.locations.listView;
	if (id) {
		return `${path}#${id}`;
	}
	return generatePath(path);
};

/**
 * AddKeycloakUser Path
 * @returns {string} path
 */
const getAddKeycloakUserPath = (): string => {
	return generatePath(ROUTES.settings.organization.users.add);
};

/**
 * EditKeycloakUser create path
 * @param {string} id
 * @returns {string} path
 */
const getEditKeycloakUserPath = (id: string): string => {
	return generatePath(ROUTES.settings.organization.users.edit, { id });
};

/**
 * Payment new request Form Path
 * @returns {string} path
 */
const paymentRequestFormPath = (params: Record<string, any>): string => {
	const pagePath = generatePath(ROUTES.payments.request);
	if (!_.isEmpty(params)) {
		const queryParams = _.chain(params)
			.pickBy(_.identity)
			.map((value, key) => `${key}=${encodeURIComponent(value)}`)
			.join('&')
			.value();
		return `${pagePath}?${queryParams}`;
	}
	return pagePath;
};

// todo
/**
 * Accounts path
 * @param {string} tab
 * @returns {string} path
 */
const getAccountsProductPath = (tab?: string): string => {
	return generatePath(ROUTES.settings.organization.subscriptions, { tab });
};

/**
 * Settings path
 * @returns {string} path
 */
const getSettingsPath = (): string => {
	return generatePath(ROUTES.settings.landing);
};

/**
 * get help path
 * @returns {string} path
 */
const getUserHelpPath = (key?: string): string => {
	const searchParams = key ? `?${searchParamsKeys.helpQuestion}=${encodeURIComponent(key)}` : '';
	return `${generatePath(ROUTES.help.landing)}${searchParams}`;
};

const getAllCodesPath = (): string => {
	return generatePath(ROUTES.codeExplorer.allCodes);
};

/**
 * get code search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getCodeSearchResultsPath = (searchTerm: string): string => {
	return generatePath(ROUTES.codeExplorer.searchResults, { searchTerm: encodeURIComponent(searchTerm) });
};

/**
 * get code set search results path
 * @returns {string} path
 */
const getCodeSetSearchResultsPath = (params: { codeSet: CodeExplorer.ICodeSetSystem; searchTerm: string }): string => {
	return generatePath(ROUTES.codeExplorer.codeSetSearchResults, params);
};

/**
 * get code list search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getCodeListSearchResultsPath = ({
	searchTerm,
	// TODO : add dynamic code set later
	codeSet = CODE_SET_MAPPING.ICD10PCS,
}: {
	searchTerm: string;
	codeSet?: CodeExplorer.ICodeSetSystem;
}): string => {
	return generatePath(ROUTES.codeExplorer.codeList, { codeSet, searchTerm });
};

/**
 * get favorite code search results path
 * @param {string} searchTerm
 * @returns {string} path
 */
const getFavoriteCodeSearchResultsPath = (searchTerm: string): string => {
	return generatePath(ROUTES.codeExplorer.favoriteCodes, { searchTerm: encodeURIComponent(searchTerm) });
};

/**
 * get code detailed view path
 * @returns {string} path
 */
const getCodeDetailedViewPath = ({ codeSet, code, tab }: { codeSet: CodeExplorer.ICodeSetSystem; code: string; tab?: string }): string => {
	return generatePath(ROUTES.codeExplorer.codeDetails, { codeSet, code, tab });
};

/**
 * get quick pay path
 * @returns {string} path
 */
const getQuickPayPath = (): string => {
	return generatePath(ROUTES.payments.quickPay);
};

/**
 * get payments integration path
 * @returns {string} path
 */
const getPaymentsIntegrationPath = (): string => {
	return generatePath(ROUTES.settings.integration.payment);
};

/**
 * get NPI Registry details path
 * @param {string} npi
 * @returns {string} path
 */
const getNpiRegistryDetailsPath = (npi: string): string => {
	return `https://npiregistry.cms.hhs.gov/provider-view/${npi}`;
};

export {
	ELIGIBILITY_REQUESTS_TABS,
	ELIGIBILITY_REQUESTS_TAB_KEYS,
	validateEligibilityRequestsTab,
	searchParamsKeys,
	newRequestKeys,
	getLogoutOptions,
	getLogoutPath,
	getHomePath,
	getEditEligibilityRequestPath,
	getEligibilityResponsePath,
	getEditPayerDetailsPath,
	getAddProviderPath,
	getEditProviderPath,
	getProviderPath,
	getAddLocationPath,
	getEditLocationPath,
	getLocationPath,
	paymentRequestFormPath,
	getAddKeycloakUserPath,
	getEditKeycloakUserPath,
	getEditBatchRequestPath,
	getBatchResponsePath,
	getEligibilityRequestsPath,
	getEligibilityNewRequestPath,
	getBatchEligibilityRequestPath,
	getNewEligibilityRequestPath,
	getClaimsIdsFromSearch,
	getClaimsRequestPath,
	getClaimsNewRequestPath,
	getEditClaimRequestPath,
	getClaimResponsePath,
	getAccountsProductPath,
	getSettingsPath,
	getManagePayersPath,
	getUserHelpPath,
	claimStatusInputFormPath,
	getAllCodesPath,
	getCodeSearchResultsPath,
	getCodeSetSearchResultsPath,
	getCodeDetailedViewPath,
	getFavoriteCodeSearchResultsPath,
	getCodeListSearchResultsPath,
	getQuickPayPath,
	getPaymentsIntegrationPath,
	getNpiRegistryDetailsPath,
};
